import React, { useMemo, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../shadcn-ui/drawer';
import { Button } from '../shadcn-ui/button';
import { useMasterStore } from '@/stores/useMaster.store';
import { Lock, Square, SquareCheckBig } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { yupResolver } from '@hookform/resolvers/yup';
import { addDryingBedSchema } from '@/schema/drying-lot-schema';
import { useForm } from 'react-hook-form';
import { addDryingBedToExistingLot } from '@/network/api.service';

const formatBedLabel = (prefix, bedNumber) => `${prefix}${String(bedNumber).padStart(2, '0')}`;

const AddDryingBedDrawer = ({ dryingLots, fetchLotData, stage }) => {
  const [open, setOpen] = useState(false);
  const beds = useMasterStore(state => state.dryingBeds);
  const fetchDryingBeds = useMasterStore(state => state.fetchDryingBeds);

  const form = useForm({
    resolver: yupResolver(addDryingBedSchema),
    defaultValues: {
      beds: [],
    },
  });

  const { setValue, watch } = form;

  const selectedBeds = watch('beds');

  const handleOpenChange = newOpen => {
    setOpen(newOpen);
    if (newOpen) {
      // Blur any focused elements in the background when drawer opens
      const activeElement = document.activeElement;
      if (activeElement && activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    }
  };

  const selectedLotIds = useMemo(() => new Set((dryingLots || []).map(lot => lot._id.toString())), [dryingLots]);
  const isSelectedInForm = (bed_number, bed_position) =>
    selectedBeds.some(b => b.bed_number === bed_number && b.bed_position === bed_position);

  const getSlotState = (slot, bed_number, bed_position) => {
    if (isSelectedInForm(bed_number, bed_position)) {
      return 'form-selected';
    }

    if (!slot) return 'locked';

    const slotLotId = slot.drying_lot_id?.toString();

    // ✅ Selected
    if (slotLotId && selectedLotIds.has(slotLotId)) {
      return 'selected';
    }

    // ⬜ Empty
    if (slot.is_empty && !slotLotId) {
      return 'empty';
    }

    if (slot.days === null) {
      return 'empty';
    }

    // 🔒 Locked
    return 'locked';
  };

  const isAlreadySelected = (bed_number, bed_position) => {
    return selectedBeds.some(b => b.bed_number === bed_number && b.bed_position === bed_position);
  };

  const toggleBed = (bed_number, bed_position, state) => {
    if (isAlreadySelected(bed_number, bed_position)) {
      setValue(
        'beds',
        selectedBeds.filter(b => !(b.bed_number === bed_number && b.bed_position === bed_position)),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
      return;
    }

    if (state === 'selected') {
      toast('Bed already in use', {
        description: 'Process not ended on this bed',
      });
      return;
    }

    if (state === 'locked') return;

    if (state === 'empty') {
      if (isAlreadySelected(bed_number, bed_position)) return;

      const lot = dryingLots[0];

      const newBed = {
        lot_id: lot.lot_id,
        lot_number: lot.lot_number,
        lotname: lot.lotname,
        estate_id: lot.estate_id,
        bed_number,
        bed_position,
        drying_start_time: lot.drying_start_time,
      };

      setValue('beds', [...selectedBeds, newBed], {
        shouldDirty: true,
        shouldValidate: true,
      });

      return;
    }
  };

  const onSubmit = async data => {
    try {
      const response = await addDryingBedToExistingLot(data.beds);

      if (response.data.success) {
        toast.success(response.data.message);
        fetchLotData();
        setOpen(false);
        form.reset();
        setValue('beds', []);
        fetchDryingBeds();
      }
    } catch (error) {
      toast.error('Error adding drying bed');
    }
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button onClick={() => setOpen(true)} disabled={stage !== 'Drying'}>
          Add bed
        </Button>
      </DrawerTrigger>

      <DrawerContent className="flex max-h-[90vh] flex-col">
        <DrawerHeader className="flex-shrink-0 p-0 text-left">
          <DrawerTitle className="hidden">Add Bed</DrawerTitle>
          <DrawerDescription className="hidden"></DrawerDescription>
        </DrawerHeader>

        <div tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto p-4">
          {/* form / content */}
          <div className="grid grid-cols-3 gap-x-2 gap-y-3 overflow-y-auto rounded-2xl border-2 border-[#E5E7EB] p-4">
            {beds?.map(bed => {
              return (
                <div key={bed._id} className="rounded border border-[#E5E7EB]">
                  {/* Upper */}
                  <BedSlot
                    label={formatBedLabel('U', bed.bed_number)}
                    state={getSlotState(bed.upper_bed, bed.bed_number, 'upper')}
                    onClick={() => toggleBed(bed.bed_number, 'upper', getSlotState(bed.upper_bed))}
                  />

                  {/* Middle */}
                  <BedSlot
                    label={formatBedLabel('M', bed.bed_number)}
                    state={getSlotState(bed.middle_bed, bed.bed_number, 'middle')}
                    onClick={() => toggleBed(bed.bed_number, 'middle', getSlotState(bed.middle_bed))}
                  />

                  {/* Lower */}
                  <BedSlot
                    label={formatBedLabel('L', bed.bed_number)}
                    state={getSlotState(bed.lower_bed, bed.bed_number, 'lower')}
                    onClick={() => toggleBed(bed.bed_number, 'lower', getSlotState(bed.lower_bed))}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <DrawerFooter className={'flex flex-row justify-end gap-2 border-t'}>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setOpen(false);
              form.reset();
            }}
          >
            Cancel
          </Button>
          <Button type="button" onClick={form.handleSubmit(onSubmit)}>
            Add bed
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const BedSlot = ({ label, state, onClick }) => {
  if (state === 'locked') {
    return (
      <div className="flex items-center gap-2 bg-[#F9FAFB] p-2 text-[#9CA3AF]">
        <Lock className="h-4 w-4" />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <label
      onClick={onClick}
      className={cn(
        'flex cursor-pointer items-center gap-2 p-2',
        (state === 'selected' || state === 'form-selected') && 'bg-[#1F2937] text-white'
      )}
    >
      {state === 'selected' || state === 'form-selected' ? (
        <SquareCheckBig className="h-4 w-4" />
      ) : (
        <Square className="h-4 w-4" />
      )}
      <span>{label}</span>
    </label>
  );
};

export default AddDryingBedDrawer;
